import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore, Store } from '@ngxs/store';
import { AdministrationState } from '@taxora-accountant-administration/data-access/state/adminstration.state';
import { AuthState } from '@taxora-accountant-auth/data-access/state/auth-state';
import { AppState } from '@taxora-accountant-core/data-access/state/app.state';
import { environment } from '@taxora-accountant/environments/environment';
import { provideApiConfiguration } from '@taxora-api';
import {
  ACCESS_TOKEN,
  httpErrorInterceptor,
  loginInterceptor,
} from '@taxora-shared-core/data-access';
import { MultiTranslateLoader } from '@taxora-shared-core/i18n';
import { Language, TAXORA_PRESET } from '@taxora-shared-core/util';

import { ProfileState } from '@taxora-accountant-profile/data-access/state/profile.state';
import { ClientsState } from '@taxora-accountant-clients/data-access/state/clients.state';
import { ActivityState } from '@taxora-accountant-clients/data-access/state/activity.state';
import { AccountantDocumentsState } from '@taxora-accountant-clients/data-access/state/documents.state';
import { InvitationsState } from '@taxora-accountant-invitations/data-access/state/invitations.state';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { firstValueFrom } from 'rxjs';
import { routes } from './app.routes';

function translateLoaderFactory(http: HttpClient): TranslateLoader {
  return new MultiTranslateLoader(http, [
    { projectName: 'taxora-shared', moduleName: 'forms' },
    { projectName: 'taxora-shared', moduleName: 'common' },
    { projectName: 'taxora-accountant', moduleName: 'auth' },
    { projectName: 'taxora-accountant', moduleName: 'core' },
    { projectName: 'taxora-accountant', moduleName: 'administration' },
    { projectName: 'taxora-accountant', moduleName: 'profile' },
    { projectName: 'taxora-accountant', moduleName: 'clients' },
    { projectName: 'taxora-accountant', moduleName: 'invitations' },
  ]);
}

function initTranslations(translate: TranslateService) {
  translate.setFallbackLang(Language.Ro);
  return firstValueFrom(translate.use(Language.Ro));
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: ACCESS_TOKEN,
      useFactory: () => () => inject(Store).selectSnapshot(AppState.accessToken),
    },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([loginInterceptor, httpErrorInterceptor])),
    provideApiConfiguration(environment.apiUrl),
    providePrimeNG({
      theme: {
        preset: TAXORA_PRESET,
      },
    }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: translateLoaderFactory,
        deps: [HttpClient],
      },
      fallbackLang: Language.Ro,
    }),
    provideStore(
      [
        AppState,
        AuthState,
        AdministrationState,
        ProfileState,
        ClientsState,
        ActivityState,
        AccountantDocumentsState,
        InvitationsState,
      ],
      withNgxsLoggerPlugin(),
      withNgxsReduxDevtoolsPlugin(),
      withNgxsStoragePlugin({
        keys: ['app.accessToken', 'app.user', 'app.currentOrganizationId'],
      }),
    ),
    MessageService,
    provideAppInitializer(() => initTranslations(inject(TranslateService))),
  ],
};
